'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { Loader2, Check, Gamepad2 } from 'lucide-react'
import { supabase, TABLES } from '@/lib/supabase'
import { getTournamentChatId } from '@/lib/tournament-chat'
import { addUserGame, updateUserGame } from '@/lib/api/user-games'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  ApexNameHelp,
  APEX_PLATFORM_HINT,
  APEX_PLATFORM_LABEL,
  type ApexHelpDestination,
} from '@/components/apex/apex-name-help'
import {
  APEX_GAME_NAME,
  APEX_PLATFORM_OPTIONS,
  APEX_CODE_TO_PLATFORM,
  verifyApexPlayer,
  type ApexCandidate,
  type ApexPlatformCode,
} from '@/lib/apex-candidate'

/** Where the user's Apex link stands. `unknown` until the lookup resolves. */
export type ApexLinkState = 'unknown' | 'linked' | 'missing'

/**
 * Does this user already have an Apex in-game ID?
 *
 * A user_games row on its own is only a game preference — it's the player_id
 * that makes the account usable for a tournament, so that is the bar here.
 * Returns the existing row id (if any) so a later save can update in place
 * instead of inserting a duplicate.
 */
export async function fetchApexLink(userId: string): Promise<{
  state: ApexLinkState
  userGameId: string | null
  gameId: string | null
}> {
  const { data: game } = await supabase
    .from(TABLES.GAMES)
    .select('id')
    .eq('name', APEX_GAME_NAME)
    .maybeSingle()

  const gameId = game?.id ?? null
  if (!gameId) return { state: 'unknown', userGameId: null, gameId: null }

  const { data: row } = await supabase
    .from(TABLES.USER_GAMES)
    .select('id, player_id')
    .eq('user_id', userId)
    .eq('game_id', gameId)
    .maybeSingle()

  return {
    state: row?.player_id?.trim() ? 'linked' : 'missing',
    userGameId: row?.id ?? null,
    gameId,
  }
}

/**
 * Inline "connect your Apex account" prompt, shown after joining an Apex
 * tournament when the user has no in-game ID on file.
 *
 * Deliberately soft: registration has already succeeded by the time this
 * renders, so dismissing it costs the user nothing. It occupies the same slot
 * as the password form for the same reason — it's a follow-up to the join, not
 * a gate in front of it.
 */
export function ApexLinkPrompt({
  tournamentId,
  locale,
  userId,
  userGameId,
  gameId,
  onLinked,
  onDismiss,
}: {
  tournamentId: string
  locale: string
  userId: string
  userGameId: string | null
  gameId: string | null
  onLinked: () => void
  onDismiss: () => void
}) {
  const [playerId, setPlayerId] = useState('')
  const [platform, setPlatform] = useState<ApexPlatformCode>('PC')
  const [candidate, setCandidate] = useState<ApexCandidate | null>(null)
  const [lookupError, setLookupError] = useState<string | null>(null)
  const [looking, setLooking] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const [chatId, setChatId] = useState<string | null>(null)

  const abortRef = useRef<AbortController | null>(null)

  // The event chat is where to ask for help when the lookup can't find them.
  // They've just joined, so the chat exists and they're a member.
  useEffect(() => {
    let cancelled = false
    getTournamentChatId(tournamentId).then((id) => {
      if (!cancelled) setChatId(id)
    })
    return () => {
      cancelled = true
    }
  }, [tournamentId])

  // Debounced lookup, mirroring the profile editor: verification is advisory,
  // so a failed lookup never blocks saving.
  useEffect(() => {
    const player = playerId.trim()
    if (!player) {
      setCandidate(null)
      setLookupError(null)
      setLooking(false)
      return
    }

    const timer = setTimeout(async () => {
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller
      setLooking(true)
      const { candidate: found, error } = await verifyApexPlayer(
        player,
        platform,
        controller.signal
      )
      if (controller.signal.aborted) return
      setCandidate(found)
      setLookupError(error)
      setLooking(false)
    }, 500)

    return () => clearTimeout(timer)
  }, [playerId, platform])

  useEffect(() => () => abortRef.current?.abort(), [])

  const save = useCallback(async () => {
    const player = playerId.trim()
    if (!player || saving) return
    setSaving(true)
    setSaveError(null)

    const mappedPlatform = APEX_CODE_TO_PLATFORM[platform]
    const res = userGameId
      ? await updateUserGame(userGameId, userId, {
          player_id: player,
          platform: mappedPlatform,
        })
      : gameId
        ? await addUserGame(userId, gameId, player, mappedPlatform)
        : { success: false, error: 'Apex Legends is unavailable right now.' }

    if (res.success) {
      onLinked()
    } else {
      setSaveError(res.error || 'Could not save your Apex ID.')
      setSaving(false)
    }
  }, [playerId, platform, saving, userGameId, gameId, userId, onLinked])

  const chatHref = chatId ? `/${locale}/app/matches/group/${chatId}` : null
  const help: ApexHelpDestination = chatHref
    ? { kind: 'chat', href: chatHref }
    : { kind: 'support' }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        save()
      }}
      className="flex w-full flex-col gap-2"
    >
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Check className="size-3.5 shrink-0 text-emerald-400" />
        You&apos;re registered. Add your Apex username so the host can find you.
      </p>

      <div className="flex w-full items-center gap-2">
        <div className="relative min-w-0 flex-1 animate-in fade-in-0 slide-in-from-right-4 duration-300 ease-out">
          <input
            type="text"
            autoFocus
            value={playerId}
            onChange={(e) => setPlayerId(e.target.value)}
            placeholder="Apex username"
            aria-label="Apex username"
            className="h-10 w-full rounded-xl border border-border bg-background pl-3.5 pr-[7.5rem] text-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-primary"
          />
          <div className="absolute inset-y-0 right-1.5 flex items-center gap-1">
            {APEX_PLATFORM_OPTIONS.map((option) => {
              const selected = platform === option.value
              return (
                <Badge
                  key={option.value}
                  asChild
                  variant="outline"
                  className={`cursor-pointer px-2 py-1 text-[11px] transition-colors ${
                    selected
                      ? 'border-primary text-primary'
                      : 'border-border text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setPlatform(option.value)}
                    aria-pressed={selected}
                  >
                    {option.label}
                  </button>
                </Badge>
              )
            })}
          </div>
        </div>
        <Button type="submit" disabled={!playerId.trim() || saving}>
          {saving ? <Loader2 className="size-4 animate-spin" /> : null}
          <span className="truncate">Save</span>
        </Button>
      </div>

      {/* Lookup feedback — advisory only, never blocks the save. */}
      {looking ? (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Loader2 className="size-3.5 animate-spin" />
          Looking up player…
        </p>
      ) : candidate ? (
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <Gamepad2 className="size-3.5 shrink-0 text-emerald-400" />
          <span className="truncate">
            Verified · Level {candidate.accountLevel.toLocaleString()}
            {candidate.rankName ? ` · ${candidate.rankName}` : ''}
          </span>
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">
          {lookupError === 'Player not found'
            ? `No ${APEX_PLATFORM_LABEL[platform]} account found. `
            : lookupError
              ? "Couldn't check that name. "
              : `${APEX_PLATFORM_HINT[platform]} `}
          <ApexNameHelp
            platform={platform}
            help={help}
            label={lookupError ? 'Get help' : 'Help'}
          />
        </p>
      )}

      {saveError && <p className="text-xs text-red-400">{saveError}</p>}

      <button
        type="button"
        onClick={onDismiss}
        className="self-start text-xs text-muted-foreground underline-offset-2 transition-colors hover:text-foreground hover:underline"
      >
        I&apos;ll do this later
      </button>
    </form>
  )
}
